import {
  allocationPreferenceLabel,
  feedstockCategoryLabel,
  toAllocationPreferenceArray,
} from '@/config/formOptions';
import { outgoingShipmentStatusLabel, allocationStatusLabel } from '@/config/statuses';
import { formatDateOrDash } from '@/lib/format/date';
import { formatTextOrDash } from '@/lib/format/text';
import {
  formatNumber,
  formatPercentOrDash,
  formatQuantity,
  formatQuantityOrDash,
  formatSecondaryQuantities,
} from '@/lib/format/number';
import { unitSymbol } from '@/lib/mappers/units';
import { buildPendingOrderFiltersInput, processFilterOptions } from '@/lib/mappers/filters';
import {
  buildQuantitiesByUnit,
  buildRawQuantitiesByUnit,
  defaultUnitType,
} from '@/lib/mappers/quantities';
import { formValue } from '@/lib/mappers/formValues';
import { formatDate } from '@/lib/format/date';
import { splitReferenceId } from '@/lib/utils/id';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { FACILITY_KINDS, buildFacilityValue, parseFacilityValue } from '@/config/formOptions';
import { ORDER_SORT_FIELDS } from '../constants/orders.constants';
import { orderStatusLabel } from '../constants/orderStatus';
import { orderDisplayStatusLabel } from '../constants/orderDisplayStatus';

// DTO ⇄ view model: the `pendingOrders` GraphQL query for the list, REST for the writes.

// The server ORs PendingOrderSearch's keys, so one box searches all three.
const buildPendingOrderSearchInput = (search) => {
  const term = search?.trim();
  if (!term) return undefined;
  const text = { contains: term, mode: 'insensitive' };
  return { orderNo: text, customerAccount: { companyname: text }, material: { name: text } };
};

/** Screen state → `pendingOrders` variables. Unlike REST, `orderStatus` takes a list. */
export const buildPendingOrdersQuery = ({
  certificateType,
  page,
  pageSize,
  sortBy,
  sortDir,
  search,
  status,
  materialId,
  customerAccountId,
  extraFilters,
} = {}) => {
  const sortField = ORDER_SORT_FIELDS[sortBy];
  return {
    certificateType,
    page,
    limit: pageSize,
    // An unsupported sort field would fail the query.
    orderBy: sortField ? [{ field: sortField, direction: sortDir ?? 'asc' }] : undefined,
    search: buildPendingOrderSearchInput(search),
    filters: buildPendingOrderFiltersInput({ status, materialId, customerAccountId }, extraFilters),
  };
};

export const processOrderFilterOptions = (result) => ({
  materialOptions: processFilterOptions(result?.materials),
  customerOptions: processFilterOptions(result?.customers),
});

// With no separate working unit or no conversion factor, the server still names `workingUnit` but sends null figures.
export const hasWorkingFigures = (order) =>
  Boolean(order?.workingUnit) &&
  (order.contractedWorkingQuantity != null || order.contractedDeclarationQuantity == null);

// The server returns each quantity twice, once per unit, so the row can switch.
const buildUnits = (node) => ({
  [UNIT_TYPES.working]: {
    unit: hasWorkingFigures(node) ? node.workingUnit : null,
    amounts: {
      // An order's unassigned IS its remaining to ship — the linkable-order mapper agrees.
      unassignedQuantity: node.remainingToShipWorkingQuantity,
      contractedQuantity: node.contractedWorkingQuantity,
      shippedQuantity: node.shippedWorkingQuantity,
      remainingToShipQuantity: node.remainingToShipWorkingQuantity,
      allocatedQuantity: node.allocatedWorkingQuantity,
      remainingToAllocateQuantity: node.remainingToAllocateWorkingQuantity,
    },
  },
  [UNIT_TYPES.declaration]: {
    unit: node.declarationUnit,
    amounts: {
      unassignedQuantity: node.remainingToShipDeclarationQuantity,
      contractedQuantity: node.contractedDeclarationQuantity,
      shippedQuantity: node.shippedDeclarationQuantity,
      remainingToShipQuantity: node.remainingToShipDeclarationQuantity,
      allocatedQuantity: node.allocatedDeclarationQuantity,
      remainingToAllocateQuantity: node.remainingToAllocateDeclarationQuantity,
    },
  },
});

// One table row, display-ready.
export const processOrder = (node) => {
  const unitsByType = buildUnits(node);
  const byUnit = buildQuantitiesByUnit(unitsByType);
  return {
    id: node.id,
    orderNo: formatTextOrDash(node.orderNo),
    // TODO: no linked-shipment count on the list DTO, as on the linkable-order row.
    totalShipmentsLinked: '-',
    materialName: formatTextOrDash(node.material?.name),
    // The three the Link shipments wizard matches candidates on, and its per-unit ceiling.
    materialId: node.material?.id ?? null,
    siteId: node.site?.id ?? null,
    customerAccountId: node.customerAccount?.id ?? null,
    unassignedByUnit: {
      [UNIT_TYPES.working]: node.remainingToShipWorkingQuantity ?? null,
      [UNIT_TYPES.declaration]: node.remainingToShipDeclarationQuantity ?? null,
    },
    facilityName: formatTextOrDash(node.site?.siteName ?? node.backToBackAccount?.accountName),
    terminal: formatTextOrDash(node.terminal),
    status: orderStatusLabel(node.orderStatus) ?? '',
    soldTo: formatTextOrDash(node.customerAccount?.companyname),
    shippingTo: formatTextOrDash(node.shippingDetails?.shippingTo),
    allocatedGhgSavings: formatPercentOrDash(node.avgGhgSaving),
    byUnit,
    // The link wizard deducts what step 2 assigns from unassigned, which needs the number.
    rawByUnit: buildRawQuantitiesByUnit(unitsByType),
    unitType: defaultUnitType(byUnit),
    tolerance: formatPercentOrDash(node.tolerance),
    maxGhgEmissions: node.ghgEmission == null ? '-' : formatNumber(node.ghgEmission),
    minGhgSavings: formatPercentOrDash(node.ghgSaving),
    rawMaterialType: formatTextOrDash(node.rawMaterialType),
    countryOfOrigin: formatTextOrDash(node.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(node.feedstockCategory)),
    // pending_order.preferredAllocation is a single scalar, not a list — never assume an array.
    allocationPreference: formatTextOrDash(
      toAllocationPreferenceArray(node.preferredAllocation)
        .map(allocationPreferenceLabel)
        .join(', '),
    ),
    dispatchBy: formatDateOrDash(node.dispatchDate),
    discrepancyCount: node.discrepancyCount ?? 0,
    hasAllocations: hasShippedOrAllocated(node),
  };
};

// Raw, not `byUnit`'s string, and either unit: a working-only material sends no declaration.
const hasShippedOrAllocated = (order) =>
  Number(order.shippedWorkingQuantity ?? 0) > 0 ||
  Number(order.shippedDeclarationQuantity ?? 0) > 0 ||
  Number(order.allocatedWorkingQuantity ?? 0) > 0 ||
  Number(order.allocatedDeclarationQuantity ?? 0) > 0;

/** The `pendingOrders` connection → the shape every list screen reads. */
export const processOrdersPage = (connection) => ({
  rows: (connection?.nodes ?? []).map(processOrder),
  totalCount: connection?.pageInfo?.total ?? 0,
  isEmpty: connection?.pageInfo?.isEmpty ?? false,
});

// The GraphQL PendingOrder type's own quantity fields put Working/Declaration BEFORE Quantity (contractedWorkingQuantity), unlike the REST list row above — a real naming divergence between the two backends.
const buildOrderDetailByUnit = (order) =>
  buildQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: hasWorkingFigures(order) ? order.workingUnit : null,
      amounts: {
        contractedQuantity: order.contractedWorkingQuantity,
        shippedQuantity: order.shippedWorkingQuantity,
        remainingToShipQuantity: order.remainingToShipWorkingQuantity,
        allocatedQuantity: order.allocatedWorkingQuantity,
        remainingToAllocateQuantity: order.remainingToAllocateWorkingQuantity,
      },
    },
    [UNIT_TYPES.declaration]: {
      unit: order.declarationUnit,
      amounts: {
        contractedQuantity: order.contractedDeclarationQuantity,
        shippedQuantity: order.shippedDeclarationQuantity,
        remainingToShipQuantity: order.remainingToShipDeclarationQuantity,
        allocatedQuantity: order.allocatedDeclarationQuantity,
        remainingToAllocateQuantity: order.remainingToAllocateDeclarationQuantity,
      },
    },
  });

// "90 MT (100 m³)" — the working figure only when the material tracks a different unit.
const formatDeclarationWithWorking = (
  declarationAmount,
  workingAmount,
  declarationUnit,
  workingUnit,
) => {
  const secondary =
    workingUnit && workingUnit !== declarationUnit && workingAmount != null
      ? formatSecondaryQuantities([[formatQuantity({ amount: workingAmount }), workingUnit]])
      : '';
  return [formatQuantityOrDash(declarationAmount, declarationUnit), secondary]
    .filter(Boolean)
    .join(' ');
};

// Order Details tab, meta bar and header discrepancy modal — GraphQL query PendingOrder($id).
export const processOrderDetail = (order) => {
  const byUnit = buildOrderDetailByUnit(order);
  const workingUnit = hasWorkingFigures(order) ? unitSymbol(order.workingUnit) : null;
  const declarationUnit = order.declarationUnit ? unitSymbol(order.declarationUnit) : null;
  return {
    id: order.id,
    orderNo: formatTextOrDash(order.orderNo),
    materialName: formatTextOrDash(order.material?.name),
    // The three the Link shipments wizard matches candidates on, and its per-unit ceiling.
    materialId: order.material?.id ?? null,
    siteId: order.site?.id ?? null,
    customerAccountId: order.customerAccount?.id ?? null,
    unassignedByUnit: {
      [UNIT_TYPES.working]: order.remainingToShipWorkingQuantity ?? null,
      [UNIT_TYPES.declaration]: order.remainingToShipDeclarationQuantity ?? null,
    },
    facilityName: formatTextOrDash(order.site?.siteName ?? order.backToBackAccount?.accountName),
    terminal: formatTextOrDash(order.terminal),
    // The computed display status ("Active"/"Closed"/"Cancelled"), not the raw orderStatus enum the list column uses — the header badge's tone is keyed by this code, not by the label.
    statusCode: order.status ?? null,
    statusLabel: formatTextOrDash(orderDisplayStatusLabel(order.status)),
    soldTo: formatTextOrDash(order.customerAccount?.companyname),
    shippingTo: formatTextOrDash(order.shippingDetails?.shippingTo),
    dispatchBy: formatDateOrDash(order.dispatchDate),
    byUnit,
    // workingUnit/declarationUnit are scalars over GraphQL, not {unitName} objects like REST.
    workingUnit,
    declarationUnit,
    // Linked outgoing shipment tab tiles — declaration first, independent of the table's unit switch.
    linkedShipmentsSummary: {
      shippedQuantity: formatDeclarationWithWorking(
        order.shippedDeclarationQuantity,
        order.shippedWorkingQuantity,
        declarationUnit,
        workingUnit,
      ),
      remainingToShipQuantity: formatDeclarationWithWorking(
        order.remainingToShipDeclarationQuantity,
        order.remainingToShipWorkingQuantity,
        declarationUnit,
        workingUnit,
      ),
    },
    // Allocation details tab tiles — declaration first, independent of the table's unit switch.
    allocationsSummary: {
      allocatedQuantity: formatDeclarationWithWorking(
        order.allocatedDeclarationQuantity,
        order.allocatedWorkingQuantity,
        declarationUnit,
        workingUnit,
      ),
      remainingToAllocateQuantity: formatDeclarationWithWorking(
        order.remainingToAllocateDeclarationQuantity,
        order.remainingToAllocateWorkingQuantity,
        declarationUnit,
        workingUnit,
      ),
    },
    tolerance: formatPercentOrDash(order.tolerance),
    maxGhgEmissions: order.ghgEmission == null ? '-' : formatNumber(order.ghgEmission),
    minGhgSavings: formatPercentOrDash(order.ghgSaving),
    rawMaterialType: formatTextOrDash(order.rawMaterialType),
    countryOfOrigin: formatTextOrDash(order.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(order.feedstockCategory)),
    // pending_order.preferredAllocation is a single scalar, not a list — never assume an array.
    allocationPreference: formatTextOrDash(
      toAllocationPreferenceArray(order.preferredAllocation)
        .map(allocationPreferenceLabel)
        .join(', '),
    ),
    // { maxGhgEmission, minGhgSaving, rawMaterialType, countryOfOrigin } booleans — drives the Mandatory badges.
    mandatory: order.mandatory ?? {},
    // The order has no itemized discrepancies type, only this 0|1 flag — see orderDiscrepancy.js.
    discrepancyCount: order.discrepancyCount ?? 0,
    // Gates Delete in the header menu, as on the list row.
    hasAllocations: hasShippedOrAllocated(order),
    // Quantity-weighted saving of the non-revoked allocations, computed server-side; "-" until one exists.
    allocatedGhgSavings: formatPercentOrDash(order.avgGhgSaving),
  };
};

// OrderLinkedShipment's own fields (assignedQuantity*, workingUnit/declarationUnit, avgGhgSaving, tolerance) are order-scoped; everything else comes off its nested `shipment`, whose quantity fields put Working/Declaration AFTER Quantity — the opposite order from PendingOrder above.
const buildLinkedShipmentByUnit = (node, shipment) =>
  buildQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: node.workingUnit,
      amounts: {
        assignedQuantity: node.assignedQuantityWorking,
        shipmentQuantity: shipment.totalQuantityWorking,
        pendingQuantity: shipment.pendingQuantityWorking,
        allocatedQuantity: shipment.allocatedQuantityWorking,
        remainingToAllocateQuantity: shipment.remainingToAllocateQuantityWorking,
      },
    },
    [UNIT_TYPES.declaration]: {
      unit: node.declarationUnit,
      amounts: {
        assignedQuantity: node.assignedQuantityDeclaration,
        shipmentQuantity: shipment.totalQuantityDeclaration,
        pendingQuantity: shipment.pendingQuantityDeclaration,
        allocatedQuantity: shipment.allocatedQuantityDeclaration,
        remainingToAllocateQuantity: shipment.remainingToAllocateQuantityDeclaration,
      },
    },
  });

// One row of the Linked Outgoing Shipment tab — GraphQL query LinkedOutgoingShipmentsForOrder($orderId), one `OrderLinkedShipment` node per row.
export const processOrderLinkedShipment = (node, index) => {
  const shipment = node.shipment ?? {};
  const byUnit = buildLinkedShipmentByUnit(node, shipment);
  return {
    id: shipment.id,
    serialNo: index + 1,
    shipmentId: formatTextOrDash(shipment.shipmentNo),
    status: outgoingShipmentStatusLabel(shipment.status) ?? '',
    shippingTo: formatTextOrDash(shipment.shippingDetails?.shippingTo),
    // Order-scoped, resolved on OrderLinkedShipment itself — not the shipment's own (all-orders) average.
    allocatedGhgSavings: formatPercentOrDash(node.avgGhgSaving),
    tolerance: formatPercentOrDash(node.tolerance),
    byUnit,
    unitType: defaultUnitType(byUnit),
    dateOfDispatch: formatDateOrDash(shipment.dispatchDate),
    discrepancyCount: shipment.discrepancyCount ?? 0,
  };
};

// The Allocation details tab's `byUnit` — one allocation's certified quantity.
const buildOrderAllocationByUnit = (dto) =>
  buildQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: dto.workingUnit,
      amounts: { certifiedQuantity: dto.certifiedQuantityWorking },
    },
    [UNIT_TYPES.declaration]: {
      unit: dto.declarationUnit,
      amounts: { certifiedQuantity: dto.certifiedQuantityDeclaration },
    },
  });

// The order/shipment this allocation was made against.
const buildAllocationReference = (dto) =>
  formatTextOrDash(dto.reference?.orderNo ?? dto.reference?.shipmentNo);

// One row of the Allocation details tab — GraphQL query AllocationDetailsForOrder($orderId), one `Fulfillment` node per row.
export const processOrderAllocation = (dto, index) => {
  const byUnit = buildOrderAllocationByUnit(dto);
  return {
    id: dto.id,
    serialNo: index + 1,
    transactionId: formatTextOrDash(dto.transactionId),
    status: allocationStatusLabel(dto.approvalStatus) ?? formatTextOrDash(dto.approvalStatus),
    statusCode: dto.approvalStatus ?? null,
    canGenerateDeclaration: Boolean(dto.canGenerateDeclaration),
    canRevoke: Boolean(dto.canRevoke),
    facilityName: formatTextOrDash(dto.sentFrom),
    sendTo: formatTextOrDash(dto.receivedAt),
    shippingTo: formatTextOrDash(dto.shippingTo),
    shippingToSites: formatTextOrDash(dto.shippingToSites),
    transactionDate: formatDateOrDash(dto.transactionDate),
    byUnit,
    unitType: defaultUnitType(byUnit),
    massBalancePeriod: formatTextOrDash(dto.massBalancePeriodLabel),
    reference: buildAllocationReference(dto),
    associatedDeclarationNo: formatTextOrDash(dto.associatedDeclarationNo),
    transactedBy: formatTextOrDash(dto.transactedBy?.name || dto.transactedBy?.email),
    countryOfOrigin: formatTextOrDash(dto.countryOfOrigin),
    rawMaterialType: formatTextOrDash(dto.rawMaterialType),
    ghgEmissions: dto.ghgEmission == null ? '-' : formatNumber(dto.ghgEmission),
    ghgEmissionSavings: formatPercentOrDash(dto.ghgEmissionSavings),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(dto.feedstockCategory)),
    allocatedQuota: formatTextOrDash(dto.allocatedQuota),
    dateOfDispatch: formatDateOrDash(dto.dateOfDispatch),
    daysLeft: dto.daysLeft ?? null,
  };
};

// Unit symbol → already-shipped quantity, the Edit quantity floor.
const buildOrderFloorByUnit = (order) => {
  const floor = {};
  const working = unitSymbol(order.workingUnit);
  const declaration = unitSymbol(order.declarationUnit);
  if (working) floor[working] = order.shippedWorkingQuantity ?? 0;
  if (declaration) floor[declaration] = order.shippedDeclarationQuantity ?? 0;
  return floor;
};

const buildAllocatedFloorByUnit = (order) => {
  const floor = {};
  const working = unitSymbol(order.workingUnit);
  const declaration = unitSymbol(order.declarationUnit);
  if (working) floor[working] = order.allocatedWorkingQuantity ?? 0;
  if (declaration) floor[declaration] = order.allocatedDeclarationQuantity ?? 0;
  return floor;
};

// Either a site or a back-to-back account, never both.
const processOrderFacilityId = (order) => {
  if (order.backToBackAccountId != null) {
    return buildFacilityValue(FACILITY_KINDS.backToBack, order.backToBackAccountId);
  }
  if (order.site?.id != null) {
    return buildFacilityValue(FACILITY_KINDS.site, order.site.id);
  }
  return '';
};

// GET (GraphQL) pendingOrder(id) → what <OrderForm> needs to prefill Edit.
export const processOrderForm = (order) => ({
  values: {
    ...splitReferenceId(order.orderNo),
    facilityId: processOrderFacilityId(order),
    terminalId: formValue(order.terminalRefId),
    materialId: formValue(order.material?.id),
    soldToId: formValue(order.customerAccount?.id),
    shippingToId: formValue(order.shippingDetails?.id),
    tolerance: formValue(order.tolerance),
    dateOfDispatch: formatDate(order.dispatchDate, 'yyyy-MM-dd'),
    // PendingOrder has no single entered-quantity/unit pair; default to the declaration variant until the backend exposes which unit was entered.
    contractedQuantity: formValue(order.contractedDeclarationQuantity),
    unit: formValue(unitSymbol(order.declarationUnit)),
    density: formValue(order.conversionFactor),
    ghgEmissions: formValue(order.ghgEmission),
    ghgEmissionSavings: formValue(order.ghgSaving),
    rawMaterialType: formValue(order.rawMaterialType),
    countryOfOrigin: formValue(order.countryOfOrigin),
    feedstockCategory: formValue(order.feedstockCategory),
    allocationPreference: toAllocationPreferenceArray(order.preferredAllocation),
    mandatoryMaxGhgEmission: Boolean(order.mandatory?.maxGhgEmission),
    mandatoryMinGhgSaving: Boolean(order.mandatory?.minGhgSaving),
    mandatoryRawMaterialType: Boolean(order.mandatory?.rawMaterialType),
    mandatoryCountryOfOrigin: Boolean(order.mandatory?.countryOfOrigin),
  },
  // Once anything is shipped or allocated, only the contracted quantity stays editable.
  isLocked:
    Number(order.shippedWorkingQuantity ?? 0) > 0 ||
    Number(order.shippedDeclarationQuantity ?? 0) > 0 ||
    Number(order.allocatedWorkingQuantity ?? 0) > 0 ||
    Number(order.allocatedDeclarationQuantity ?? 0) > 0,
  shippedByUnit: buildOrderFloorByUnit(order),
  allocatedByUnit: buildAllocatedFloorByUnit(order),
});

// Form values → POST/PATCH /:module/pending-orders(/:id).
export const buildOrderDto = (values, { equivalentUnit } = {}) => {
  const facility = parseFacilityValue(values.facilityId);
  const isSite = facility.kind === FACILITY_KINDS.site;

  return {
    orderNo: [values.idPrefix, values.idNumber].filter(Boolean).join('-') || undefined,
    siteId: isSite ? Number(facility.id) : undefined,
    backToBackAccountId: isSite ? undefined : Number(facility.id),
    // `terminal`, not `terminalId`: this route reads the id under that key; null clears it.
    terminal: isSite && values.terminalId ? Number(values.terminalId) : null,
    materialId: Number(values.materialId),
    customerAccountId: Number(values.soldToId),
    shippingDetailsId: values.shippingToId ? Number(values.shippingToId) : undefined,
    contractedQuantity: Number(values.contractedQuantity),
    contractedQuantityUnit: values.unit || undefined,
    displayUnitId: values.displayUnitId ? Number(values.displayUnitId) : undefined,
    conversionFactor: values.density || undefined,
    equivalentUnit: values.density ? equivalentUnit : undefined,
    tolerance: values.tolerance ? Number(values.tolerance) : undefined,
    dispatchDate: values.dateOfDispatch,
    ghgEmission: values.ghgEmissions ? Number(values.ghgEmissions) : undefined,
    // The route reads `ghgSaving`; it ignores a `ghgEmissionSavings` key.
    ghgSaving: values.ghgEmissionSavings ? Number(values.ghgEmissionSavings) : undefined,
    rawMaterialType: values.rawMaterialType || undefined,
    countryOfOrigin: values.countryOfOrigin || undefined,
    feedstockCategory: values.feedstockCategory || undefined,
    // The server rejects an empty array, so nothing selected omits the field entirely.
    preferredAllocation: values.allocationPreference?.length
      ? values.allocationPreference
      : undefined,
    // Flat keys: the route reads `maxGhgEmissionMandatory` etc. and ignores a nested `mandatory`.
    maxGhgEmissionMandatory: Boolean(values.mandatoryMaxGhgEmission),
    minGhgSavingMandatory: Boolean(values.mandatoryMinGhgSaving),
    rawMaterialTypeMandatory: Boolean(values.mandatoryRawMaterialType),
    countryOfOriginMandatory: Boolean(values.mandatoryCountryOfOrigin),
  };
};
